import { Title } from '@color/continuum';
import { invert } from 'lodash-es';

import { AppointmentType } from 'ui/common/types';
import { MultiServiceClinicSupportedAppointmentType, PatientPreference } from 'ui/Covid/types';

import {
  PrevaccinationChecklistName,
  VaccineDose,
  VaccineServiceName,
  VaccineTypeSERE,
} from './types';

export const MONTHS_IN_YEAR = 12;
export const WEEKS_IN_YEAR = 52;

export const GROUP_REGISTRATION_MAX_PEOPLE = 8;

// This maps from VaccineType to the manufacturer names used by the immunization
// schedule library and the availability_if_eligible endpoint.
// FIXME: We should properly resolve the weird relationship between
// PriorPatientVaccine and VaccineType.
export const IMMUNIZATION_SCHEDULE_VACCINE_MANUFACTURER_BY_VACCINE_TYPE_VALUE: {
  [key in VaccineTypeSERE]: string;
} = {
  [VaccineTypeSERE.PFIZER_BIONTECH]: 'Pfizer-BioNTech',
  [VaccineTypeSERE.PFIZER_BIONTECH_2024_2025]: 'Pfizer-BioNTech',
  [VaccineTypeSERE.PFIZER_BIONTECH_BIVALENT]: 'Pfizer-BioNTech Bivalent',
  [VaccineTypeSERE.MODERNA]: 'Moderna',
  [VaccineTypeSERE.MODERNA_2024_2025]: 'Moderna',
  [VaccineTypeSERE.MODERNA_BIVALENT]: 'Moderna Bivalent',
  [VaccineTypeSERE.JANSSEN]: 'Janssen',
  [VaccineTypeSERE.BHARAT_BIOTECH]: 'Bharat Biotech',
  [VaccineTypeSERE.CANSINO]: 'CanSino',
  [VaccineTypeSERE.NOVAVAX]: 'Novavax',
  [VaccineTypeSERE.NOVAVAX_2023_2024]: 'Novavax',
  [VaccineTypeSERE.OXFORD_ASTRAZENECA]: 'Oxford/AstraZeneca',
  [VaccineTypeSERE.SINOPHARM]: 'Sinopharm',
  [VaccineTypeSERE.SINOVAC]: 'Sinovac',
};

// We don't track appointment duration, but the UI needs to show the duration sometimes. Hardcode.
export const DEFAULT_VACCINE_REGISTRATION_APPOINTMENT_DURATION = { minutes: 5 };

export const ONSITE_TOOL_QUERY_PARAM = 'originIsOnsiteTool';

export const MINIMUM_INELIGIBLE_FOR_VFC_AGE = 19;

export const VACCINE_DOSES_FOR_APPOINTMENT_MANAGEMENT = [VaccineDose.FIRST, VaccineDose.SECOND];

export const VACCINATION_CHECKLIST_ALLERGIC_REACTIONS_KEY = 'allergic_reactions';
export const VACCINATION_CHECKLIST_HEALTH_CONDITIONS_KEY = 'health_conditions';
export const VACCINATION_CHECKLIST_MONKEYPOX_HEALTH_CONDITIONS_KEY = 'monkeypox_health_conditions';
export const VACCINATION_CHECKLIST_MONKEYPOX_ALLERGIES_KEY = 'monkeypox_allergies';

// Last update based off of CDC guideline:
// https://www.cdc.gov/vaccines-for-children/media/pdfs/2024/08/vfc-ops-guide_version-4-0-july-2024_low-res-508.pdf
export const VFC_ELIGIBLE_VACCINE_SERVICE_NAMES = new Set([
  VaccineServiceName.COVID_19_VACCINE,
  VaccineServiceName.DENGUE_VACCINE,
  VaccineServiceName.DTAP_DT_VACCINE,
  VaccineServiceName.TDAP_TD_VACCINE,
  VaccineServiceName.HEPATITIS_A_HEPATITIS_B_VACCINE,
  VaccineServiceName.HEPATITIS_A_VACCINE,
  VaccineServiceName.HEPATITIS_B_VACCINE,
  VaccineServiceName.HIB_VACCINE,
  VaccineServiceName.HPV_VACCINE,
  VaccineServiceName.INFLUENZA_VACCINE,
  VaccineServiceName.MENINGOCOCCAL_ABCWY_VACCINE,
  VaccineServiceName.MENINGOCOCCAL_VACCINE,
  VaccineServiceName.MENINGOCOCCAL_B_VACCINE,
  VaccineServiceName.MMR_VACCINE,
  VaccineServiceName.MMRV_VACCINE,
  VaccineServiceName.PNEUMOCOCCAL_VACCINE,
  VaccineServiceName.POLIO_VACCINE,
  VaccineServiceName.ROTAVIRUS_VACCINE,
  VaccineServiceName.RSV_VACCINE,
  VaccineServiceName.VARICELLA_VACCINE,
  VaccineServiceName.VACCINIA_VACCINE,
  // Obsolete service names
  VaccineServiceName.DTAP_TD_TDAP_VACCINE,
  VaccineServiceName.DTAP_VACCINE,
  VaccineServiceName.TD_VACCINE,
]);

export const VACCINE_DOSE_TO_HUMAN_READABLE_STRING: Record<VaccineDose, string> = {
  [VaccineDose.FIRST]: 'first',
  [VaccineDose.SECOND]: 'second',
  [VaccineDose.THIRD]: 'third',
  [VaccineDose.ADDITIONAL]: 'additional',
  [VaccineDose.BOOSTER]: 'monovalent booster',
  [VaccineDose.SECOND_BOOSTER]: 'monovalent second booster',
  [VaccineDose.BIVALENT_BOOSTER]: 'bivalent booster',
};

export const VACCINE_SERVICE_NAME_TO_APPOINTMENT_TYPE: Record<
  VaccineServiceName,
  MultiServiceClinicSupportedAppointmentType
> = {
  [VaccineServiceName.ADENO_VACCINE]: AppointmentType.ADENO_VACCINE,
  [VaccineServiceName.ANTRAX_VACCINE]: AppointmentType.ANTRAX_VACCINE,
  [VaccineServiceName.BCG_VACCINE]: AppointmentType.BCG_VACCINE,
  [VaccineServiceName.CHIKUNGUNYA_VACCINE]: AppointmentType.CHIKUNGUNYA_VACCINE,
  [VaccineServiceName.CHOLERA_VACCINE]: AppointmentType.CHOLERA_VACCINE,
  [VaccineServiceName.COVID_19_VACCINE]: AppointmentType.COVID_19_VACCINE,
  [VaccineServiceName.DENGUE_VACCINE]: AppointmentType.DENGUE_VACCINE,
  [VaccineServiceName.DTAP_HEP_B_IPV_VACCINE]: AppointmentType.DTAP_HEP_B_IPV_VACCINE,
  [VaccineServiceName.DTAP_HIB_IPV_VACCINE]: AppointmentType.DTAP_HIB_IPV_VACCINE,
  [VaccineServiceName.DTAP_IPV_HIB_HEP_B_VACCINE]: AppointmentType.DTAP_IPV_HIB_HEP_B_VACCINE,
  [VaccineServiceName.DTAP_IPV_VACCINE]: AppointmentType.DTAP_IPV_VACCINE,
  [VaccineServiceName.DTAP_TD_TDAP_VACCINE]: AppointmentType.DTAP_TD_TDAP_VACCINE,
  [VaccineServiceName.DTAP_VACCINE]: AppointmentType.DTAP_VACCINE,
  [VaccineServiceName.DTAP_DT_VACCINE]: AppointmentType.DTAP_DT_VACCINE,
  [VaccineServiceName.EBOLA_VACCINE]: AppointmentType.EBOLA_VACCINE,
  [VaccineServiceName.HEPATITIS_A_HEPATITIS_B_VACCINE]:
    AppointmentType.HEPATITIS_A_HEPATITIS_B_VACCINE,
  [VaccineServiceName.HEPATITIS_A_VACCINE]: AppointmentType.HEPATITIS_A_VACCINE,
  [VaccineServiceName.HEPATITIS_B_VACCINE]: AppointmentType.HEPATITIS_B_VACCINE,
  [VaccineServiceName.HIB_VACCINE]: AppointmentType.HIB_VACCINE,
  [VaccineServiceName.HPV_VACCINE]: AppointmentType.HPV_VACCINE,
  [VaccineServiceName.INFLUENZA_VACCINE]: AppointmentType.INFLUENZA_VACCINE,
  [VaccineServiceName.IN_PERSON_CONSULT]: AppointmentType.IN_PERSON_CONSULT,
  [VaccineServiceName.JAPANESE_ENCEPHALITIS_VACCINE]: AppointmentType.JAPANESE_ENCEPHALITIS_VACCINE,
  [VaccineServiceName.MENINGOCOCCAL_ABCWY_VACCINE]: AppointmentType.MENINGOCOCCAL_ABCWY_VACCINE,
  [VaccineServiceName.MENINGOCOCCAL_B_VACCINE]: AppointmentType.MENINGOCOCCAL_B_VACCINE,
  [VaccineServiceName.MENINGOCOCCAL_VACCINE]: AppointmentType.MENINGOCOCCAL_VACCINE,
  [VaccineServiceName.MMR_VACCINE]: AppointmentType.MMR_VACCINE,
  [VaccineServiceName.MMRV_VACCINE]: AppointmentType.MMRV_VACCINE,
  [VaccineServiceName.PNEUMOCOCCAL_VACCINE]: AppointmentType.PNEUMOCOCCAL_VACCINE,
  [VaccineServiceName.POLIO_VACCINE]: AppointmentType.POLIO_VACCINE,
  [VaccineServiceName.RABIES_VACCINE]: AppointmentType.RABIES_VACCINE,
  [VaccineServiceName.ROTAVIRUS_VACCINE]: AppointmentType.ROTAVIRUS_VACCINE,
  [VaccineServiceName.RSV_VACCINE]: AppointmentType.RSV_VACCINE,
  [VaccineServiceName.TD_VACCINE]: AppointmentType.TD_VACCINE,
  [VaccineServiceName.TDAP_TD_VACCINE]: AppointmentType.TDAP_TD_VACCINE,
  [VaccineServiceName.TICK_BORNE_ENCEPHALITIS_VACCINE]:
    AppointmentType.TICK_BORNE_ENCEPHALITIS_VACCINE,
  [VaccineServiceName.TYPHOID_VACCINE]: AppointmentType.TYPHOID_VACCINE,
  [VaccineServiceName.VACCINIA_VACCINE]: AppointmentType.VACCINIA_VACCINE,
  [VaccineServiceName.VARICELLA_VACCINE]: AppointmentType.VARICELLA_VACCINE,
  [VaccineServiceName.YELLOW_FEVER_VACCINE]: AppointmentType.YELLOW_FEVER_VACCINE,
  [VaccineServiceName.ZOSTER_VACCINE]: AppointmentType.ZOSTER_VACCINE,
  // Non-vaccine services for multiservice clinics
  [VaccineServiceName.CHLAMYDIA_GONORRHEA_TEST]: AppointmentType.CHLAMYDIA_GONORRHEA,
};

export const APPOINTMENT_TYPE_TO_VACCINE_SERVICE_NAME: Record<
  MultiServiceClinicSupportedAppointmentType,
  VaccineServiceName
> = invert(VACCINE_SERVICE_NAME_TO_APPOINTMENT_TYPE) as Record<
  MultiServiceClinicSupportedAppointmentType,
  VaccineServiceName
>;

export const PREVACCINATION_CHECKLIST_TO_VACCINE_SERVICE_NAME: Record<
  PrevaccinationChecklistName,
  VaccineServiceName[]
> = {
  [PrevaccinationChecklistName.GENERAL]: [
    VaccineServiceName.ADENO_VACCINE,
    VaccineServiceName.ANTRAX_VACCINE,
    VaccineServiceName.BCG_VACCINE,
    VaccineServiceName.CHIKUNGUNYA_VACCINE,
    VaccineServiceName.CHOLERA_VACCINE,
    VaccineServiceName.COVID_19_VACCINE,
    VaccineServiceName.DENGUE_VACCINE,
    VaccineServiceName.DTAP_HEP_B_IPV_VACCINE,
    VaccineServiceName.DTAP_HIB_IPV_VACCINE,
    VaccineServiceName.DTAP_IPV_HIB_HEP_B_VACCINE,
    VaccineServiceName.DTAP_IPV_VACCINE,
    VaccineServiceName.DTAP_TD_TDAP_VACCINE,
    VaccineServiceName.DTAP_DT_VACCINE,
    VaccineServiceName.DTAP_VACCINE,
    VaccineServiceName.EBOLA_VACCINE,
    VaccineServiceName.HEPATITIS_A_VACCINE,
    VaccineServiceName.HEPATITIS_B_VACCINE,
    VaccineServiceName.HIB_VACCINE,
    VaccineServiceName.HPV_VACCINE,
    VaccineServiceName.JAPANESE_ENCEPHALITIS_VACCINE,
    VaccineServiceName.MENINGOCOCCAL_ABCWY_VACCINE,
    VaccineServiceName.MENINGOCOCCAL_B_VACCINE,
    VaccineServiceName.MENINGOCOCCAL_VACCINE,
    VaccineServiceName.MMR_VACCINE,
    VaccineServiceName.MMRV_VACCINE,
    VaccineServiceName.PNEUMOCOCCAL_VACCINE,
    VaccineServiceName.POLIO_VACCINE,
    VaccineServiceName.RABIES_VACCINE,
    VaccineServiceName.ROTAVIRUS_VACCINE,
    VaccineServiceName.RSV_VACCINE,
    VaccineServiceName.TD_VACCINE,
    VaccineServiceName.TDAP_TD_VACCINE,
    VaccineServiceName.TICK_BORNE_ENCEPHALITIS_VACCINE,
    VaccineServiceName.TYPHOID_VACCINE,
    VaccineServiceName.VARICELLA_VACCINE,
    VaccineServiceName.YELLOW_FEVER_VACCINE,
    VaccineServiceName.ZOSTER_VACCINE,
  ],
  [PrevaccinationChecklistName.FLU]: [VaccineServiceName.INFLUENZA_VACCINE],
  [PrevaccinationChecklistName.MONKEYPOX_FIRST_DOSE]: [VaccineServiceName.VACCINIA_VACCINE],
  [PrevaccinationChecklistName.MONKEYPOX_SECOND_DOSE]: [VaccineServiceName.VACCINIA_VACCINE],
};

export const PATIENT_PREFERENCE_LABELS: Record<PatientPreference, string> = {
  [PatientPreference.COVID_19_JANSSEN_VACCINES]: 'Johnson & Johnson/Janssen',
  [PatientPreference.COVID_19_MODERNA_VACCINES]: 'Moderna',
  [PatientPreference.COVID_19_NOVAVAX_VACCINES]: 'Novavax',
  [PatientPreference.COVID_19_PFIZER_VACCINES]: 'Pfizer-BioNTech',
  // These are unused - needed for the Record typing
  [PatientPreference.FLU_NASAL_SPRAY_VACCINES]: 'Nasal spray',
  [PatientPreference.FLU_STANDARD_VACCINES]: 'Standard',
  [PatientPreference.CHLAMYDIA_GONORRHEA_1_SITE_TEST]: 'Chlamydia and gonorrhea one site test',
  [PatientPreference.CHLAMYDIA_GONORRHEA_3_SITE_TEST]: 'Chlamydia and gonorrhea three site test',
};

export const ADMIN_VACCINE_AVAILABILITIES_URL = '/api/v1/vaccine_availabilities';
export const VACCINE_APPOINTMENT_URL = '/api/v1/vaccination_appointments';
// Path to use for appointments that isn't vaccines specific
// Will be useful as we migrate off of VaccinationAppointmentViewSet
export const ONSITE_APPOINTMENT_URL = '/api/v1/onsite_appointments';

const infoPaneHeadingOverride = {
  component: Title,
  props: {
    variant: 'headline4',
    component: 'h3',
  },
};

export const INFO_PANE_MARKDOWN_OVERRIDES = {
  h1: infoPaneHeadingOverride,
  h2: infoPaneHeadingOverride,
  h3: infoPaneHeadingOverride,
};
